// ===========================================================================
// The sign-in proof of work, found away from the main thread.
//
// Past a username's sign-in budget the server asks a device it does not know
// for work instead of a wait (server/src/login-work.js). account.js asks this
// for the counter: in a module Web Worker where one starts, and otherwise on
// this thread in stretches that yield between them, so the page never freezes
// while it runs. No DOM and no config import, so this runs under the node test
// runner, which has no Worker and takes the stretches. ASCII only.
// ===========================================================================

import { createSearch, validDifficulty } from './login-work-solver.js';

/** How many challenges one sign-in answers before it leaves the server's answer for the form. */
export const LOGIN_WORK_TRIES = 3;

/** Counters tried between yields when there is no worker: a few hundredths of a second on a phone. */
const STRETCH = 20000;

function inWorker(challenge, difficulty) {
  return new Promise((resolve, reject) => {
    let worker;
    try {
      worker = new Worker(new URL('./login-work.worker.js', import.meta.url), { type: 'module' });
    } catch (err) {
      reject(err);
      return;
    }
    const finish = (settle, value) => {
      worker.terminate();
      settle(value);
    };
    worker.onmessage = (event) => {
      const counter = event.data?.counter;
      if (Number.isSafeInteger(counter) && counter >= 0) finish(resolve, counter);
      else finish(reject, new Error('work_failed'));
    };
    worker.onerror = (event) => {
      event?.preventDefault?.();
      finish(reject, new Error('worker_failed'));
    };
    worker.onmessageerror = () => finish(reject, new Error('worker_failed'));
    worker.postMessage({ challenge, difficulty });
  });
}

async function inStretches(challenge, difficulty) {
  const search = createSearch(challenge, difficulty);
  for (;;) {
    const counter = search.next(STRETCH);
    if (counter >= 0) return counter;
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
}

/**
 * The counter that does the work for this challenge.
 * @returns {Promise<number>} rejects for a challenge or difficulty it cannot use
 */
export async function solveLoginWork(challenge, difficulty) {
  if (typeof challenge !== 'string' || !challenge || !validDifficulty(difficulty)) throw new Error('bad_work');
  if (typeof Worker === 'function') {
    try {
      return await inWorker(challenge, difficulty);
    } catch {
      // A worker that cannot start (a content policy, an old browser) is no
      // reason to fail the sign-in: the work is done here instead.
    }
  }
  return inStretches(challenge, difficulty);
}

export default { solveLoginWork, LOGIN_WORK_TRIES };
